<script>
import axios from "axios";

export default {
  data() {
    return {
      step: 1,
      email: "",
      otp: "",
      password: "",
      confirmPassword: "",
      loading: false,
      error: "",
      successMessage: "",
      countdown: 0,
      canResend: false,
      timer: null,
    };
  },
  created() {
    document.body.classList.add("auth-body-bg");
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
  computed: {
    formattedCountdown() {
      const m = Math.floor(this.countdown / 60);
      const s = this.countdown % 60;
      return `${m}:${s.toString().padStart(2, "0")}`;
    },
  },
  methods: {
    startCountdown() {
      this.countdown = 180;
      this.canResend = false;
      if (this.timer) clearInterval(this.timer);
      this.timer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.timer);
          this.canResend = true;
        }
      }, 1000);
    },

    async sendOtp() {
      this.error = "";
      if (!this.email) {
        this.error = "Email is required";
        return;
      }

      this.loading = true;
      try {
        await axios.post(
          process.env.VUE_APP_API_BACKEND_URL + "auth/send-otp",
          { email: this.email }
        );
        this.step = 2;
        this.startCountdown();
        this.successMessage = "OTP sent to your email";
        setTimeout(() => (this.successMessage = ""), 3000);
      } catch (err) {
        this.error =
          err.response?.data?.message || "Failed to send OTP";
      } finally {
        this.loading = false;
      }
    },

    async resendOtp() {
      this.error = "";
      this.otp = "";
      this.loading = true;
      try {
        await axios.post(
          process.env.VUE_APP_API_BACKEND_URL + "auth/send-otp",
          { email: this.email }
        );
        this.startCountdown();
        this.successMessage = "OTP resent to your email";
        setTimeout(() => (this.successMessage = ""), 3000);
      } catch (err) {
        this.error =
          err.response?.data?.message || "Failed to resend OTP";
      } finally {
        this.loading = false;
      }
    },

    async verifyOtp() {
      this.error = "";
      if (!this.otp) {
        this.error = "OTP is required";
        return;
      }

      this.loading = true;
      try {
        await axios.post(
          process.env.VUE_APP_API_BACKEND_URL + "auth/verify-otp",
          { email: this.email, otp: this.otp }
        );
        this.step = 3;
        this.successMessage = "OTP verified";
        setTimeout(() => (this.successMessage = ""), 3000);
      } catch (err) {
        this.error =
          err.response?.data?.message || "OTP verification failed";
      } finally {
        this.loading = false;
      }
    },

    async resetPassword() {
      this.error = "";
      if (!this.password) {
        this.error = "Password is required";
        return;
      }
      if (this.password.length < 6) {
        this.error = "Password must be at least 6 characters";
        return;
      }
      if (this.password !== this.confirmPassword) {
        this.error = "Passwords do not match";
        return;
      }

      this.loading = true;
      try {
        await axios.post(
          process.env.VUE_APP_API_BACKEND_URL + "auth/reset-password",
          {
            email: this.email,
            otp: this.otp,
            password: this.password,
            password_confirmation: this.confirmPassword,
          }
        );
        this.$router.push("/login");
      } catch (err) {
        this.error =
          err.response?.data?.message || "Password reset failed";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<template>
  <div>
    <div class="home-btn d-none d-sm-block">
      <a href="/">
        <i class="mdi mdi-home-variant h2 text-white"></i>
      </a>
    </div>
    <div>
      <div class="container-fluid p-0">
        <div class="row no-gutters">
          <div class="col-lg-12">
            <div
              class="authentication-page-content p-4 d-flex align-items-center min-vh-100"
            >
              <div class="w-100">
                <div class="row justify-content-center">
                  <div class="col-lg-4">
                    <div>
                      <div class="text-center">
                        <div>
                          <a href="/" class="logo">
                            <img
                              src="@/assets/Dil-Foods-new-logo.png"
                              height="100"
                              alt="logo"
                            />
                          </a>
                        </div>

                        <h4 class="font-size-18 mt-4">Reset Password</h4>
                        <p class="text-muted">
                          <span v-if="step === 1">Enter your email to receive an OTP</span>
                          <span v-if="step === 2">Enter the OTP sent to your email</span>
                          <span v-if="step === 3">Set your new password</span>
                        </p>
                      </div>

                      <!-- Step Indicator -->
                      <div class="d-flex align-items-center justify-content-center mb-4 mt-3">
                        <span
                          class="badge badge-pill d-inline-flex align-items-center justify-content-center"
                          :class="step >= 1 ? 'badge-danger' : 'badge-secondary'"
                          style="width: 30px; height: 30px;"
                        >
                          <i v-if="step > 1" class="mdi mdi-check"></i>
                          <span v-else>1</span>
                        </span>
                        <div
                          class="mx-2"
                          :style="{ width: '40px', height: '2px', backgroundColor: step > 1 ? '#dc3545' : '#dee2e6' }"
                        ></div>
                        <span
                          class="badge badge-pill d-inline-flex align-items-center justify-content-center"
                          :class="step >= 2 ? 'badge-danger' : 'badge-secondary'"
                          style="width: 30px; height: 30px;"
                        >
                          <i v-if="step > 2" class="mdi mdi-check"></i>
                          <span v-else>2</span>
                        </span>
                        <div
                          class="mx-2"
                          :style="{ width: '40px', height: '2px', backgroundColor: step > 2 ? '#dc3545' : '#dee2e6' }"
                        ></div>
                        <span
                          class="badge badge-pill d-inline-flex align-items-center justify-content-center"
                          :class="step >= 3 ? 'badge-danger' : 'badge-secondary'"
                          style="width: 30px; height: 30px;"
                        >3</span>
                      </div>

                      <!-- Alerts -->
                      <b-alert
                        v-if="error"
                        variant="danger"
                        class="mt-3"
                        show
                        dismissible
                        @dismissed="error = ''"
                      >{{ error }}</b-alert>

                      <b-alert
                        v-if="successMessage"
                        variant="success"
                        class="mt-3"
                        show
                      >{{ successMessage }}</b-alert>

                      <div class="p-2 mt-3">
                        <!-- Step 1: Email -->
                        <form
                          v-if="step === 1"
                          class="form-horizontal"
                          @submit.prevent="sendOtp"
                        >
                          <div class="form-group auth-form-group-custom mb-4">
                            <i class="ri-mail-line auti-custom-input-icon"></i>
                            <label for="email">Email</label>
                            <input
                              type="email"
                              v-model="email"
                              class="form-control"
                              id="email"
                              placeholder="Enter email"
                            />
                          </div>
                          <div class="mt-4 text-center">
                            <button
                              class="btn btn-danger w-md waves-effect waves-light"
                              type="submit"
                              :disabled="loading"
                            >
                              {{ loading ? "Sending..." : "Send OTP" }}
                            </button>
                          </div>
                        </form>

                        <!-- Step 2: OTP -->
                        <form
                          v-if="step === 2"
                          class="form-horizontal"
                          @submit.prevent="verifyOtp"
                        >
                          <div class="form-group auth-form-group-custom mb-4">
                            <i class="ri-lock-password-line auti-custom-input-icon"></i>
                            <label for="otp">OTP</label>
                            <input
                              type="text"
                              v-model="otp"
                              class="form-control"
                              id="otp"
                              placeholder="Enter 6-digit OTP"
                              maxlength="6"
                            />
                          </div>

                          <div class="d-flex justify-content-between align-items-center mb-3">
                            <small class="text-muted">
                              {{ countdown > 0 ? `Expires in ${formattedCountdown}` : "OTP expired" }}
                            </small>
                            <a
                              href="javascript:void(0)"
                              @click="resendOtp"
                              :class="{ 'text-muted': !canResend || loading }"
                              :style="{ pointerEvents: (!canResend || loading) ? 'none' : 'auto' }"
                            >Resend OTP</a>
                          </div>

                          <div class="mt-4 text-center">
                            <button
                              class="btn btn-danger w-md waves-effect waves-light"
                              type="submit"
                              :disabled="loading || countdown === 0"
                            >
                              {{ loading ? "Verifying..." : "Verify OTP" }}
                            </button>
                          </div>
                        </form>

                        <!-- Step 3: New Password -->
                        <form
                          v-if="step === 3"
                          class="form-horizontal"
                          @submit.prevent="resetPassword"
                        >
                          <div class="form-group auth-form-group-custom mb-4">
                            <i class="ri-lock-2-line auti-custom-input-icon"></i>
                            <label for="password">New Password</label>
                            <input
                              type="password"
                              v-model="password"
                              class="form-control"
                              id="password"
                              placeholder="Enter new password"
                            />
                          </div>
                          <div class="form-group auth-form-group-custom mb-4">
                            <i class="ri-lock-2-line auti-custom-input-icon"></i>
                            <label for="confirmPassword">Confirm Password</label>
                            <input
                              type="password"
                              v-model="confirmPassword"
                              class="form-control"
                              id="confirmPassword"
                              placeholder="Confirm new password"
                            />
                          </div>
                          <div class="mt-4 text-center">
                            <button
                              class="btn btn-danger w-md waves-effect waves-light"
                              type="submit"
                              :disabled="loading"
                            >
                              {{ loading ? "Resetting..." : "Reset Password" }}
                            </button>
                          </div>
                        </form>
                      </div>

                      <div class="mt-5 text-center">
                        <p>
                          <router-link
                            to="/login"
                            class="font-weight-medium text-primary"
                          >Back to Sign In</router-link>
                        </p>
                        <p>
                          © {{ (new Date()).getFullYear() }}. Crafted with
                          <i class="mdi mdi-heart text-danger"></i> by
                          <i class="mdi mdi-heart text-danger"></i>DilFoods
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
